import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import classnames from 'classnames';
import ReactAvatarEditor, { type Props as AvatarEditorProps } from 'react-avatar-editor';

import { type InputProps } from '../Input';
import { ScaleInput } from '../ScaleInput';

import styles from './ImageCropper.module.scss';

type ImageCropperModalProps = {
    image: { src: string };
    shouldPreserveResolution?: boolean;
    width?: number;
    height?: number;
    className?: string;
    slotProps?: {
        avatarEditor?: Omit<
            AvatarEditorProps,
            'image' | 'onPositionChange' | 'position' | 'ref' | 'scale'
        >;
        input?: Omit<InputProps, 'onChange' | 'value'>;
    };
};

const DEFAULT_CROPPER_WIDTH = 390;
const DEFAULT_CROPPER_HEIGHT = 130;

export type ImageCropperPosition = {
    x: number;
    y: number;
};

export type ImageCropperRef = {
    position: ImageCropperPosition;
    scale: number;
    getDataUrl: () => string | null;
};

const ImageCropper = forwardRef<ImageCropperRef, ImageCropperModalProps>(
    (
        {
            image,
            shouldPreserveResolution = true,
            height = DEFAULT_CROPPER_HEIGHT,
            width = DEFAULT_CROPPER_WIDTH,
            className,
            slotProps: { input: inputProps, avatarEditor: avatarEditorProps } = {},
            ...rest
        }: ImageCropperModalProps,
        ref
    ) => {
        const editorRef = useRef<ReactAvatarEditor>(null);
        const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
        const [scale, setScale] = useState(1);

        const handleScaleChange = (newScale: number) => setScale(newScale);

        useImperativeHandle(
            ref,
            () => ({
                position,
                scale,
                getDataUrl: () => {
                    if (editorRef.current?.getImage || editorRef.current?.getImageScaledToCanvas) {
                        const dataURL = shouldPreserveResolution
                            ? editorRef.current.getImage()?.toDataURL?.()
                            : editorRef.current.getImageScaledToCanvas()?.toDataURL?.();
                        return dataURL ?? null;
                    }
                    return null;
                }
            }),
            [position, scale]
        );

        return (
            <div className={classnames(styles.container, className)} {...rest}>
                <ReactAvatarEditor
                    image={image?.src}
                    width={width}
                    height={height}
                    onPositionChange={setPosition}
                    position={position}
                    ref={editorRef}
                    scale={scale}
                    {...avatarEditorProps}
                />
                <div className={styles.zoomSelector}>
                    <ScaleInput
                        name="scale"
                        onChange={handleScaleChange}
                        scale={scale}
                        min="0.1"
                        max="2.5"
                        step="0.05"
                        {...inputProps}
                    />
                </div>
            </div>
        );
    }
);

ImageCropper.displayName = 'ImageCropper';

export { ImageCropper, type ImageCropperModalProps };
